/**
 * THỰC THI HOẠT ẢNH RA CHIÊU.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * BÀI HỌC LỚN (đã từng làm sai):
 * Lần đầu tôi TỰ ĐOÁN dấu góc xoay cho từng bên tay ("tay trái thì đảo dấu").
 * Sai. Trong hệ xương này, xương tay trỏ xuống theo chiều +y, nên hướng của tay là
 *
 *        hướng = (−sin θ , cos θ)
 *
 * ⇒ "đưa CẢ HAI tay về phía trước" cần CÙNG một dấu, còn "dang hai tay sang hai bên"
 * mới cần dấu đối nhau. Đoán mò thì có dáng tay trái chĩa ngược ra sau.
 *
 * Cách làm đúng: KHAI BÁO HƯỚNG MONG MUỐN (dx, dy) rồi để `dir()` tự tính góc.
 * Nhờ vậy đọc code là biết ngay tay đang chĩa đi đâu.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Mỗi dáng chia 3 nhịp: LẤY ĐÀ → RA ĐÒN → THU VỀ, kèm dịch chuyển gốc (root motion).
 *
 * ⚠️ HAI CÁI BẪY NỮA ĐÃ SẬP, ghi lại:
 * 1) Biên độ PHẢI LỚN mới đọc ra ở cỡ nhân vật ~100px. Dáng xoay hông ±0,5 rad bị
 *    người chơi phàn nàn "không thấy đá liên hoàn" — phải tới ~1,9 rad (xem kickCombo).
 * 2) Dáng nào hai tay đưa RA TRƯỚC thì PHẢI có `frontArms: true` (khai ở
 *    data/skill-anims.ts) để lớp vẽ cho tay đè lên thân — không thì tay trái
 *    (z=25) chui ra sau thân (z=30) và biến mất.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * DÁNG MỚI Ở FILE NÀY: `BONUS_CAST_ANIMS` (dragonKick · meditate · fanSweep ·
 * spearHand · doubleFist). `applyCastAnim` hiểu ngay các id đó; muốn dùng được
 * trong trận thì phải khai thêm vào `CAST_ANIMS` + `CastAnimId` ở
 * `src/data/skill-anims.ts` (file đó thuộc về agent khác — xem báo cáo).
 */
import type { Bone, Rig } from '../rig/rig';
// Chỉ ĐỌC bảng dáng cũ để biết id nào hợp lệ — không sửa file của agent khác.
import { CAST_ANIMS, type CastAnimDef } from '../data/skill-anims';

const noop = {
  name: '_', parent: null, x: 0, y: 0, rot: 0, sx: 1, sy: 1,
  local: () => ({ a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 }),
  world: { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 },
} as unknown as Bone;

/** Hệ trục: x dương = hướng nhân vật đang nhìn, y dương = xuống. */
const FWD = 1;
const BACK = -1;
const UP = -1;
const DOWN = 1;
const TAU = Math.PI * 2;

/**
 * Góc xoay để chi (tay/chân) trỏ theo hướng (dx, dy).
 * Từ hướng = (−sinθ, cosθ) ⇒ θ = atan2(−dx, dy).
 */
function dir(dx: number, dy: number): number {
  return Math.atan2(-dx, dy);
}

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}
function easeOut(x: number): number {
  const c = clamp01(x);
  return 1 - Math.pow(1 - c, 3);
}
function easeIn(x: number): number {
  const c = clamp01(x);
  return c * c;
}
function easeInOut(x: number): number {
  const c = clamp01(x);
  return c < 0.5 ? 2 * c * c : 1 - Math.pow(-2 * c + 2, 2) / 2;
}

/** Chia tiến trình 0..1 thành 3 nhịp: lấy đà · ra đòn · thu về. */
function phases(t: number, windEnd = 0.28, hitEnd = 0.62): { wind: number; hit: number; back: number } {
  const c = clamp01(t);
  return {
    wind: c < windEnd ? c / windEnd : 1,
    hit: c < windEnd ? 0 : c < hitEnd ? (c - windEnd) / (hitEnd - windEnd) : 1,
    back: c < hitEnd ? 0 : (c - hitEnd) / (1 - hitEnd),
  };
}

/** Nội suy góc giữa tư thế lấy đà và tư thế ra đòn. */
function swing(a0: number, a1: number, hit: number, wind: number, back: number): number {
  const s = easeOut(hit) * (1 - 0.3 * easeIn(back));
  const w = easeInOut(wind) * (1 - easeOut(hit));
  return a0 * w + a1 * s + a0 * 0.25 * easeIn(back) * 0.4;
}

// ────────────────────────────────────────────────────────────────────────────
// DÁNG MỚI (chưa khai trong CAST_ANIMS — xem chú thích đầu file)
// ────────────────────────────────────────────────────────────────────────────

export type BonusCastAnimId = 'dragonKick' | 'meditate' | 'fanSweep' | 'spearHand' | 'doubleFist';

/**
 * Bí danh của 5 dáng mới, trỏ THẲNG về `CAST_ANIMS` — nguồn sự thật duy nhất của
 * metadata nằm ở `src/data/skill-anims.ts`.
 *
 * ⚠️ Vì sao KHÔNG khai lại metadata ở đây: hai bản metadata sẽ lệch nhau sau vài lần
 * sửa (dur · weight · frontArms), mà `frontArms` lệch thì tay trái chui ra sau thân
 * và biến mất. Chiều import cũng an toàn: file này → skill-anims.ts, không có vòng.
 */
export const BONUS_CAST_ANIMS: Record<BonusCastAnimId, CastAnimDef> = {
  dragonKick: CAST_ANIMS.dragonKick,
  meditate: CAST_ANIMS.meditate,
  fanSweep: CAST_ANIMS.fanSweep,
  spearHand: CAST_ANIMS.spearHand,
  doubleFist: CAST_ANIMS.doubleFist,
};

export const BONUS_CAST_ANIM_IDS = Object.keys(BONUS_CAST_ANIMS) as BonusCastAnimId[];

/** `applyCastAnim` có hiểu id này không? (dáng cũ lẫn dáng mới) */
export function isKnownCastAnim(id: string): boolean {
  return id in CAST_ANIMS || Object.prototype.hasOwnProperty.call(BONUS_CAST_ANIMS, id);
}

export function applyCastAnim(rig: Rig, anim: string, t: number): void {
  const b = (n: string): Bone => rig.tryBone(n) ?? noop;
  rig.resetPose();

  const root = b('root');
  const torso = b('torso');
  const head = b('head');
  root.sx = 1;
  root.sy = 1;
  root.x = 0;
  root.y = 0;
  // `resetPose` đã trả root.rot về 0, nhưng ghi rõ ra đây để dáng xoay người
  // (dragonKick, fanSweep) không phụ thuộc vào thứ tự gọi.
  root.rot = 0;

  const { wind, hit, back } = phases(t);
  const settle = easeIn(back);
  /** độ "đang ra đòn" 0..1 */
  const S = swing(0, 1, hit, wind, back);
  const W = easeInOut(wind) * (1 - easeOut(hit));

  switch (anim) {
    // ─────────────────────────────────────────────────────── CHƯỞNG (2 tay đẩy tới)
    case 'palmPush': {
      const a0 = dir(BACK * 0.35, DOWN * 0.94); // lấy đà: 2 tay kéo về sau
      const a1 = dir(FWD, -0.06); // ra đòn: 2 tay đẩy thẳng tới
      b('shoulderR').rot = swing(a0, a1, hit, wind, back);
      b('shoulderL').rot = swing(a0, a1, hit, wind, back); // CÙNG dấu vì cùng chĩa tới
      b('elbowR').rot = 1.3 * (1 - S) + 0.05;
      b('elbowL').rot = 1.3 * (1 - S) + 0.05;
      torso.rot = -0.2 * W + 0.38 * S;
      head.rot = 0.1 * W - 0.18 * S;
      b('hipR').rot = -0.45 * S;
      b('kneeR').rot = 0.3 + 0.5 * S;
      b('hipL').rot = 0.5 * S;
      b('kneeL').rot = 0.3 + 0.35 * S;
      root.x = -4 * W + 11 * S;
      root.y = 2.5 * W - 2.5 * S - 1.5 * settle;
      root.sx = 1 - 0.05 * W + 0.06 * S;
      root.sy = 1 + 0.05 * W - 0.05 * S;
      break;
    }

    // ────────────────────────────────────────────── THƯỢNG QUYỀN (tay phải móc lên)
    case 'uppercut': {
      const r0 = dir(BACK * 0.5, DOWN * 0.86); // khuỵu xuống, tay hạ sau hông
      const r1 = dir(FWD * 0.85, UP * 0.53); // móc vọt lên trước
      const l0 = dir(FWD * 0.25, DOWN * 0.97);
      const l1 = dir(FWD * 0.15, DOWN * 0.99);
      b('shoulderR').rot = swing(r0, r1, hit, wind, back);
      b('shoulderL').rot = swing(l0, l1, hit, wind, back);
      b('elbowR').rot = 1.9 - 0.35 * S;
      torso.rot = 0.42 * W - 0.35 * S;
      head.rot = -0.25 * W - 0.16 * S;
      b('hipR').rot = -0.35 - 0.25 * S;
      b('hipL').rot = 0.4 * S;
      b('kneeR').rot = 0.2 + 1.0 * W - 0.8 * S;
      b('kneeL').rot = 0.2 + 0.8 * W - 0.6 * S;
      root.y = 11 * W - 9 * S;
      root.x = -3 * W + 5 * S;
      root.sy = 1 - 0.08 * W + 0.09 * S; // khuỵu (nén) rồi bật (giãn)
      root.sx = 1 + 0.06 * W - 0.05 * S;
      break;
    }

    // ───────────────────────────────────────── ĐẬP ĐẤT (2 tay giơ cao rồi giáng)
    case 'groundSlam': {
      const rise = easeOut(wind);
      const r0 = dir(FWD * 0.3, UP * 0.95); // giơ cao
      const r1 = dir(FWD * 0.55, DOWN * 0.84); // giáng xuống trước
      const l0 = dir(BACK * 0.3, UP * 0.95);
      const l1 = dir(BACK * 0.55, DOWN * 0.84);
      b('shoulderR').rot = r0 * rise + r1 * S;
      b('shoulderL').rot = l0 * rise + l1 * S;
      b('hipR').rot = -0.1 - 0.6 * S;
      b('hipL').rot = 0.1 + 0.6 * S;
      b('kneeR').rot = 0.1 + 1.3 * S;
      b('kneeL').rot = 0.1 + 1.3 * S;
      torso.rot = -0.4 * rise + 0.8 * S;
      head.rot = -0.34 * rise + 0.4 * S;
      root.y = -26 * rise + 15 * S; // bay lên rồi sụp xuống
      root.x = 4 * S; // giáng xuống thì cả người đổ tới
      root.sy = 1 + 0.09 * rise - 0.12 * S;
      root.sx = 1 - 0.07 * S;
      break;
    }

    // ───────────────────────────────────────── DẬM ĐẤT (1 chân nhấc cao rồi dậm)
    case 'stomp': {
      const lift = easeOut(wind);
      const leg0 = dir(FWD * 0.6, UP * 0.8); // nhấc đùi lên trước, cao hơn bản cũ
      const leg1 = dir(FWD * 0.08, DOWN * 1); // dậm thẳng xuống
      b('hipR').rot = leg0 * lift + leg1 * S;
      b('kneeR').rot = 0.2 + 1.5 * lift - 1.5 * S;
      b('hipL').rot = dir(0, DOWN);
      b('kneeL').rot = 0.12 + 0.5 * S;
      b('shoulderR').rot = swing(dir(FWD * 0.3, DOWN), dir(FWD * 0.85, UP * 0.5), hit, wind, back);
      b('shoulderL').rot = swing(dir(BACK * 0.3, DOWN), dir(BACK * 0.85, UP * 0.5), hit, wind, back);
      torso.rot = 0.25 * lift - 0.2 * S;
      head.rot = -0.12 * lift + 0.24 * S;
      root.y = -8 * lift + 8 * S;
      root.x = 3.5 * S;
      root.sy = 1 + 0.05 * lift - 0.07 * S;
      break;
    }

    // ─────────────────────────────────── KHIÊU KHÍCH (dang 2 tay, ưỡn ngực, ngả sau)
    case 'taunt': {
      const open = Math.sin(clamp01(t) * Math.PI);
      // dang sang hai bên và hơi ra sau ⇒ dấu ĐỐI nhau
      b('shoulderR').rot = dir(FWD * 0.92, DOWN * 0.38) * open;
      b('shoulderL').rot = dir(BACK * 0.92, DOWN * 0.38) * open;
      b('elbowR').rot = 0.2 * open;
      b('elbowL').rot = 0.2 * open;
      torso.rot = -0.38 * open;
      head.rot = 0.3 * open;
      b('hipL').rot = 0.24 * open;
      b('hipR').rot = -0.24 * open;
      b('kneeL').rot = 0.2 * open;
      b('kneeR').rot = 0.2 * open;
      root.y = -4 * open;
      root.x = -2 * open;
      torso.rot += settle * Math.sin(t * 60) * 0.02;
      break;
    }

    // ─────────────────────────── GÀO (2 tay đưa lên trước mặt, đầu chúi tới, rung)
    case 'shout': {
      /*
       * Bản cũ chỉ đổi `s = sin(t·π)` nên hai tay ĐỨNG YÊN một chỗ suốt dáng
       * (đo ra chỉ ~0,2 rad biến thiên). Nay có nhịp thật: hít vào co tay xuống
       * bụng (W) → đẩy hai tay lên trước mặt + chúi đầu + lao tới (S) → rung.
       */
      const a0 = dir(FWD * 0.5, DOWN * 0.87); // lấy đà: co tay xuống trước bụng
      const a1 = dir(FWD * 0.8, UP * 0.55); // ra đòn: đẩy cả hai tay lên trước mặt
      const arm = swing(a0, a1, hit, wind, back);
      b('shoulderR').rot = arm;
      b('shoulderL').rot = arm; // CÙNG dấu
      b('elbowR').rot = 1.5 * (1 - S) + 0.2;
      b('elbowL').rot = 1.5 * (1 - S) + 0.2;
      torso.rot = 0.15 * W + 0.4 * S;
      head.rot = -0.1 * W - 0.4 * S;
      b('hipR').rot = -0.35 * S;
      b('hipL').rot = 0.4 * S;
      b('kneeR').rot = 0.2 + 0.5 * S;
      b('kneeL').rot = 0.2 + 0.4 * S;
      root.x = -4 * W + 6 * S;
      root.y = 2 * W - 2 * S;
      root.sy = 1 + 0.05 * W - 0.05 * S;
      const vib = Math.sin(t * 90) * 0.035 * S;
      torso.rot += vib;
      head.rot += vib;
      break;
    }

    // ───────────────────────── NÉM LIÊN TIẾP (2 tay ném xen kẽ, 4 nhịp)
    case 'volley': {
      const reps = 4;
      const ph = (t * reps) % 1;
      const rightTurn = Math.floor(t * reps) % 2 === 0;
      // mỗi nhịp: kéo tay về sau rồi vung tới trước
      const k = ph < 0.4 ? -1 + 2 * (ph / 0.4) : 1 - 1.4 * easeOut((ph - 0.4) / 0.6);
      const fwd = dir(FWD, -0.25);
      const backD = dir(BACK * 0.55, DOWN * 0.5);
      const arm = fwd * (1 - k) / 2 + backD * (1 + k) / 2;
      b('shoulderR').rot = rightTurn ? arm : dir(FWD * 0.4, DOWN * 0.9);
      b('shoulderL').rot = rightTurn ? dir(BACK * 0.4, DOWN * 0.9) : arm;
      b('elbowR').rot = 0.9 - 0.5 * k;
      b('elbowL').rot = 0.9 + 0.5 * k;
      torso.rot = 0.22 * k;
      head.rot = -0.08;
      b('hipR').rot = -0.2 * k;
      b('hipL').rot = 0.2 * k;
      root.x = -3 + 6 * Math.abs(k);
      root.y = -1.5 * Math.abs(k);
      break;
    }

    // ───────────────────────────────── TRIỆU HỒI (2 tay giơ lên trời, ưỡn ra sau)
    case 'summon': {
      const rise = easeOut(clamp01(t / 0.7));
      const drop = easeIn(clamp01((t - 0.7) / 0.3));
      const up0 = dir(FWD * 0.32, UP * 0.95);
      const up1 = dir(BACK * 0.32, UP * 0.95);
      const down0 = dir(FWD * 0.55, DOWN * 0.84);
      const down1 = dir(BACK * 0.55, DOWN * 0.84);
      b('shoulderR').rot = up0 * rise + down0 * drop;
      b('shoulderL').rot = up1 * rise + down1 * drop;
      torso.rot = -0.45 * rise + 0.3 * drop;
      head.rot = 0.4 * rise - 0.24 * drop;
      b('hipL').rot = 0.3 * rise;
      b('hipR').rot = -0.3 * rise;
      b('kneeL').rot = 0.1 + 0.3 * rise;
      b('kneeR').rot = 0.1 + 0.3 * rise;
      root.y = -10 * rise + 6 * drop;
      root.x = 2.5 * rise;
      root.sy = 1 + 0.07 * rise;
      root.sx = 1 - 0.04 * rise;
      break;
    }

    // ───────────────────── THỦ THẾ (2 tay bắt chéo trước ngực, hạ thấp người)
    case 'guard': {
      const s = Math.sin(clamp01(t) * Math.PI);
      const a = dir(FWD * 0.85, UP * 0.55); // cả hai tay đưa lên trước ngực
      b('shoulderR').rot = a * s;
      b('shoulderL').rot = a * s; // CÙNG dấu
      b('elbowR').rot = 1.5 * s;
      b('elbowL').rot = 1.5 * s;
      torso.rot = 0.34 * s;
      head.rot = 0.18 * s;
      b('hipR').rot = -0.42 * s;
      b('hipL').rot = 0.42 * s;
      b('kneeR').rot = 0.7 * s;
      b('kneeL').rot = 0.7 * s;
      root.y = 9 * s;
      root.sy = 1 - 0.08 * s;
      root.sx = 1 + 0.05 * s;
      break;
    }

    // ───────────────────────────── LƯỚT (2 tay giật về sau rồi lao người tới)
    case 'dash': {
      const lean = S;
      b('shoulderR').rot = swing(dir(BACK * 0.6, DOWN * 0.8), dir(FWD * 0.9, DOWN * 0.45), hit, wind, back);
      b('shoulderL').rot = swing(dir(BACK * 0.6, DOWN * 0.8), dir(FWD * 0.9, DOWN * 0.45), hit, wind, back);
      torso.rot = -0.6 * (1 - lean) + 0.5 * lean;
      head.rot = -0.24;
      b('hipR').rot = swing(0.85, -0.75, hit, wind, back);
      b('hipL').rot = swing(-0.6, 0.6, hit, wind, back);
      b('kneeR').rot = 0.2 + 0.5 * (1 - lean);
      b('kneeL').rot = 0.2 + 0.4 * (1 - lean);
      root.x = -8 * (1 - lean) + 16 * lean;
      root.y = -4 * lean;
      root.sx = 1 - 0.05 * (1 - lean) + 0.08 * lean;
      root.sy = 1 + 0.05 * (1 - lean) - 0.07 * lean;
      break;
    }

    // ─────────────── LIÊN HOÀN (2 tay quét ngang liên tục + chân đá xen kẽ)
    case 'spin': {
      const reps = 3;
      const sw = Math.sin(t * reps * Math.PI * 2);
      const bob = Math.abs(sw);
      // Biên độ đã nâng lên: bản cũ chỉ ±0,5 rad nên nhìn không ra đòn.
      b('shoulderR').rot = dir(FWD * 0.95, DOWN * 0.3) + sw * 0.9;
      b('shoulderL').rot = dir(BACK * 0.95, DOWN * 0.3) - sw * 0.9;
      b('elbowR').rot = 0.35 + 0.4 * bob;
      b('elbowL').rot = 0.35 + 0.4 * bob;
      b('hipR').rot = -0.85 * sw;
      b('hipL').rot = 0.85 * sw;
      b('kneeR').rot = 0.3 + 0.6 * bob;
      b('kneeL').rot = 0.25 + 0.4 * bob;
      torso.rot = 0.3 * sw;
      head.rot = -0.14 * sw;
      root.y = -3.5 * bob;
      root.x = 4 * sw;
      root.rot = sw * 0.35; // cả người xoay theo vòng quét
      break;
    }

    // ───────────────────────── BÙNG SỨC (2 tay gồng ra hai bên, người rung)
    case 'powerUp': {
      const s = Math.sin(clamp01(t) * Math.PI);
      const vib = Math.sin(t * 80) * 0.04 * s;
      b('shoulderR').rot = dir(FWD * 0.9, UP * 0.44) * s;
      b('shoulderL').rot = dir(BACK * 0.9, UP * 0.44) * s; // dấu đối: gồng 2 bên
      b('elbowR').rot = 0.2 * s;
      b('elbowL').rot = 0.2 * s;
      torso.rot = -0.3 * s + vib;
      head.rot = -0.24 * s + vib;
      b('hipL').rot = 0.28 * s;
      b('hipR').rot = -0.28 * s;
      b('kneeL').rot = 0.4 * s;
      b('kneeR').rot = 0.4 * s;
      root.y = -3 * s + vib * 20;
      root.sy = 1 + 0.07 * s;
      root.sx = 1 - 0.07 * s;
      break;
    }

    // ───────────────────────────────── NẠP ĐẠN (2 tay thao tác ngang hông)
    case 'reload': {
      /*
       * Bản cũ chỉ nhích tay ±0,25 rad nên đo ra gần như đứng hình.
       * Nay: 3 nhịp lên đạn rõ ràng — giật tay về hai phía, chỏ gập sâu,
       * người xoay theo tay, đầu cúi nhìn băng đạn.
       */
      const ph = (t * 3) % 1;
      const tap = Math.sin(ph * Math.PI);
      const jerk = Math.sin(ph * Math.PI * 2);
      const a = dir(FWD * 0.6, DOWN * 0.8);
      b('shoulderR').rot = a - 0.55 * tap;
      b('shoulderL').rot = a + 0.55 * tap;
      b('elbowR').rot = 1.6 - 0.9 * tap;
      b('elbowL').rot = 1.6 + 0.9 * tap;
      torso.rot = 0.1 + 0.22 * jerk;
      head.rot = 0.3 * tap;
      b('hipR').rot = -0.15 * tap;
      b('hipL').rot = 0.15 * tap;
      b('kneeR').rot = 0.15 + 0.25 * tap;
      root.y = 3 * tap;
      root.x = -2 * tap;
      break;
    }

    // ──────────────── NÉM MẠNH (tay phải vung qua đầu rồi ném mạnh về trước)
    case 'hurl': {
      const r0 = dir(BACK * 0.85, UP * 0.53); // vung qua đầu ra sau
      const r1 = dir(FWD * 0.95, DOWN * 0.3); // ném mạnh về trước
      b('shoulderR').rot = r0 * easeOut(wind) + r1 * S;
      b('shoulderL').rot = swing(dir(FWD * 0.35, DOWN * 0.94), dir(BACK * 0.6, DOWN * 0.8), hit, wind, back);
      b('elbowR').rot = 1.4 * (1 - S) + 0.05;
      torso.rot = -0.38 * easeOut(wind) + 0.62 * S;
      head.rot = -0.18 * easeOut(wind) + 0.14 * S;
      b('hipR').rot = -0.35 * S;
      b('hipL').rot = 0.4 * S;
      b('kneeR').rot = 0.2 + 0.5 * S;
      root.x = -5 * easeOut(wind) + 11 * S;
      root.y = -4 * easeOut(wind) + 2.5 * S;
      root.sx = 1 + 0.06 * S;
      root.sy = 1 - 0.05 * S;
      break;
    }

    // ─────────────────── ẨN THÂN (ngồi sụp, 2 tay chạm đất rồi bật lên)
    case 'crouch': {
      const down = easeOut(clamp01(t / 0.4));
      const up = easeIn(clamp01((t - 0.55) / 0.45));
      const k = down * (1 - up);
      b('hipR').rot = dir(FWD * 0.3, DOWN * 0.95) * k;
      b('hipL').rot = dir(BACK * 0.3, DOWN * 0.95) * k;
      b('kneeR').rot = 1.7 * k;
      b('kneeL').rot = 1.7 * k;
      b('shoulderR').rot = dir(FWD * 0.6, DOWN * 0.8) * k;
      b('shoulderL').rot = dir(FWD * 0.6, DOWN * 0.8) * k; // CÙNG dấu: 2 tay chạm đất trước mặt
      b('elbowR').rot = 0.9 * k;
      b('elbowL').rot = 0.9 * k;
      torso.rot = 0.7 * k;
      head.rot = -0.35 * k;
      root.y = 18 * k;
      root.x = -2 * k;
      root.sx = 1 + 0.09 * k;
      root.sy = 1 - 0.14 * k;
      break;
    }


    // ───────────────────── ĐÁ LIÊN HOÀN (4 cú đá xen kẽ 2 chân, biên độ LỚN)
    // Đây là dáng cho "Vô Ảnh Cước". Dáng `spin` cũ chỉ xoay hông ±0,5 rad (~29°)
    // nên người chơi không thấy cú đá nào — biên độ phải lớn mới đọc ra.
    case 'kickCombo': {
      const reps = 4;
      const idx = Math.min(reps - 1, Math.floor(t * reps));
      const ph = (t * reps) % 1;
      const rightLeg = idx % 2 === 0;

      // chân trong 1 nhịp: thu → duỗi mạnh tới trước → co về
      const CHAMBER = dir(FWD * 0.5, DOWN * 0.86); // lên gối: đùi nâng về trước
      const KICK = dir(FWD * 0.94, UP * 0.34); // duỗi: đá vọt lên trước
      const STANCE = dir(FWD * 0.08, DOWN * 1);
      let legAngle: number;
      if (ph < 0.22) legAngle = STANCE + (CHAMBER - STANCE) * (ph / 0.22);
      else if (ph < 0.5) legAngle = CHAMBER + (KICK - CHAMBER) * easeOut((ph - 0.22) / 0.28);
      else legAngle = KICK + (STANCE - KICK) * easeIn((ph - 0.5) / 0.5);

      const kickAmt = ph >= 0.22 && ph < 0.5 ? easeOut((ph - 0.22) / 0.28) : 0;
      const other = dir(FWD * 0.08, DOWN * 1); // chân trụ đứng thẳng

      b('hipR').rot = rightLeg ? legAngle : other;
      b('hipL').rot = rightLeg ? other : legAngle;
      // chân trụ hơi chùng để giữ thăng bằng
      b('kneeR').rot = rightLeg ? 0.15 + 0.5 * (1 - kickAmt) : 0.25;
      b('kneeL').rot = rightLeg ? 0.25 : 0.15 + 0.5 * (1 - kickAmt);
      b('footR').rot = rightLeg ? -0.35 * kickAmt : 0;
      b('footL').rot = rightLeg ? 0 : -0.35 * kickAmt;

      // người ngả ra sau khi đá, tay quạt giữ thăng bằng
      const lean = -0.34 * kickAmt;
      torso.rot = lean + 0.08 * Math.sin(t * reps * Math.PI * 2);
      head.rot = -lean * 0.5 + 0.1;
      b('shoulderR').rot = dir(FWD * 0.55, UP * 0.55) * kickAmt + dir(FWD * 0.2, DOWN * 0.95) * (1 - kickAmt);
      b('shoulderL').rot = dir(BACK * 0.7, DOWN * 0.5) * kickAmt + dir(BACK * 0.2, DOWN * 0.95) * (1 - kickAmt);

      // mỗi cú đá nhấc người lên và lao tới rõ hơn (cú đá phải "đọc" được ở cỡ nhỏ)
      root.y = -6 * kickAmt;
      root.x = 5 * kickAmt;
      root.sy = 1 + 0.05 * kickAmt;
      root.sx = 1 + 0.03 * kickAmt;
      break;
    }

    // ───────────────────── ĐẤM LIÊN HOÀN (6 cú đấm xen kẽ, nhanh)
    // Dáng cho "Thiên Thủ Quan Âm": tay đánh liên tục chứ không phải chân.
    case 'punchCombo': {
      const reps = 6;
      const idx = Math.min(reps - 1, Math.floor(t * reps));
      const ph = (t * reps) % 1;
      const rightHand = idx % 2 === 0;

      const CHAMBER = dir(BACK * 0.55, DOWN * 0.62); // co tay về sau
      const PUNCH = dir(FWD * 0.98, UP * 0.2); // duỗi thẳng tới trước
      const GUARD = dir(FWD * 0.6, UP * 0.42);
      let armAngle: number;
      if (ph < 0.28) armAngle = GUARD + (CHAMBER - GUARD) * (ph / 0.28);
      else if (ph < 0.52) armAngle = CHAMBER + (PUNCH - CHAMBER) * easeOut((ph - 0.28) / 0.24);
      else armAngle = PUNCH + (GUARD - PUNCH) * easeIn((ph - 0.52) / 0.48);

      const ext = ph >= 0.28 && ph < 0.52 ? easeOut((ph - 0.28) / 0.24) : 0;
      b('shoulderR').rot = rightHand ? armAngle : GUARD;
      b('shoulderL').rot = rightHand ? GUARD : armAngle;

      torso.rot = 0.06 + (rightHand ? -1 : 1) * 0.26 * ext;
      head.rot = -0.08;
      b('hipR').rot = dir(FWD * 0.16, DOWN * 0.99) + 0.18 * ext * (rightHand ? -1 : 1);
      b('hipL').rot = dir(BACK * 0.16, DOWN * 0.99) - 0.18 * ext * (rightHand ? -1 : 1);
      b('kneeR').rot = 0.22 + 0.25 * ext;
      b('kneeL').rot = 0.22 + 0.2 * ext;
      root.x = 5 * ext;
      root.y = -1.5 * ext;
      break;
    }

    // ══════════════════════════════════════════════ 5 DÁNG MỚI (BONUS)

    // ───────────────────── THẦN LONG BÃI VĨ (xoay trọn 1 vòng rồi đá)
    case 'dragonKick': {
      /*
       * Xoay cả người quanh trục đứng bằng `root.rot`. Vì `root` nằm ở MẶT ĐẤT
       * nên nhân vật xoay như đang trồng chân trụ — đúng dáng đá xoay.
       * Xoay đúng −2π (một vòng trọn) rồi DỪNG ở đó: −2π ≡ 0 nên không bị giật
       * khi hết dáng. Nếu xoay 1,5 vòng thì hết chiêu nhân vật quay mặt đi.
       */
      const spin = easeOut(hit);
      root.rot = -TAU * spin + 0.5 * W; // 0,5 rad ngược lại lúc lấy đà
      const ext = easeOut(hit);
      const CHAMBER = dir(FWD * 0.6, DOWN * 0.8);
      const KICK = dir(FWD * 0.96, UP * 0.28);
      b('hipR').rot = CHAMBER + (KICK - CHAMBER) * ext;
      b('kneeR').rot = 1.5 * (1 - ext) + 0.1;
      b('footR').rot = -0.45 * ext;
      b('hipL').rot = 0.12;
      b('kneeL').rot = 0.3 + 0.3 * (1 - ext);
      b('shoulderR').rot = dir(BACK * 0.8, DOWN * 0.6) * ext + dir(FWD * 0.3, DOWN * 0.95) * (1 - ext);
      b('shoulderL').rot = dir(FWD * 0.85, UP * 0.53) * (1 - ext) + dir(BACK * 0.55, DOWN * 0.8) * ext;
      torso.rot = -0.25 * ext + 0.2 * W;
      head.rot = 0.18 * ext - 0.1 * W;
      root.y = -7 * ext + 3 * W;
      root.x = -3 * W + 6 * ext;
      root.sy = 1 + 0.06 * ext;
      root.sx = 1 - 0.04 * ext;
      break;
    }

    // ───────────────────────── THIỀN BÙNG KHÍ (ngồi kiết già → bùng khí)
    case 'meditate': {
      /*
       * 45% đầu: ngồi sụp xuống, hai gối gập ngang, hai tay chắp trước ngực.
       * 55% sau: BÙNG — người vươn thẳng, hai tay mở ra hai bên (dấu ĐỐI nhau),
       * gốc nhấc lên và giãn dọc. Ngồi → bùng phải khác hẳn nhau mới đọc ra.
       */
      const burst = easeOut(clamp01((t - 0.45) / 0.4));
      const sit = 1 - easeInOut(clamp01((t - 0.45) / 0.55));

      b('hipR').rot = dir(FWD * 0.55, DOWN * 0.84) * sit;
      b('hipL').rot = dir(BACK * 0.55, DOWN * 0.84) * sit;
      b('kneeR').rot = 1.7 * sit;
      b('kneeL').rot = 1.7 * sit;
      b('footR').rot = -0.45 * sit;
      b('footL').rot = -0.45 * sit;

      // chắp tay trước ngực (CÙNG dấu) → mở ra hai bên (ĐỐI dấu)
      const pray = dir(FWD * 0.85, UP * 0.53);
      b('shoulderR').rot = pray * sit + dir(FWD * 0.95, UP * 0.3) * burst;
      b('shoulderL').rot = pray * sit + dir(BACK * 0.95, UP * 0.3) * burst;
      b('elbowR').rot = 1.6 * sit + 0.15 * burst;
      b('elbowL').rot = 1.6 * sit + 0.15 * burst;

      torso.rot = 0.4 * sit - 0.35 * burst;
      head.rot = -0.3 * sit + 0.35 * burst;
      root.y = 17 * sit - 9 * burst;
      root.x = -2 * sit + 2.5 * burst;
      root.sy = 1 - 0.12 * sit + 0.09 * burst;
      root.sx = 1 + 0.07 * sit - 0.05 * burst;
      const vib = Math.sin(t * 70) * 0.03 * burst;
      torso.rot += vib;
      head.rot += vib;
      break;
    }

    // ───────────────────────── PHIẾN QUÉT (quạt ngang liên tục 3 vòng)
    case 'fanSweep': {
      /*
       * Hai tay quét ngược chiều nhau quanh tư thế chĩa tới, và CẢ NGƯỜI xoay
       * theo (`root.rot`) — chỉ quay tay thì ở cỡ 100px không thấy được vòng quét.
       * Biên độ ±0,9 rad quanh hướng tới ⇒ tay đi từ sau lưng ra trước mặt.
       */
      const reps = 3;
      const sw = Math.sin(t * reps * TAU);
      const bob = Math.abs(sw);
      b('shoulderR').rot = dir(FWD * 0.95, DOWN * 0.3) + sw * 0.9;
      b('shoulderL').rot = dir(BACK * 0.95, DOWN * 0.3) - sw * 0.9;
      b('elbowR').rot = 0.25 + 0.45 * bob;
      b('elbowL').rot = 0.25 + 0.45 * bob;
      b('hipR').rot = -0.4 * sw;
      b('hipL').rot = 0.4 * sw;
      b('kneeR').rot = 0.25 + 0.45 * bob;
      b('kneeL').rot = 0.2 + 0.35 * bob;
      torso.rot = 0.22 * sw;
      head.rot = -0.12 * sw;
      root.rot = sw * 0.5;
      root.x = 4 * bob;
      root.y = -3 * bob;
      root.sy = 1 + 0.04 * bob;
      break;
    }

    // ───────────────────────── THƯƠNG THỦ (chọt hai tay thẳng như ngọn giáo)
    case 'spearHand': {
      const a0 = dir(BACK * 0.35, DOWN * 0.94); // kéo sát hông
      const a1 = dir(FWD * 0.99, UP * 0.14); // chọt thẳng tới
      b('shoulderR').rot = swing(a0, a1, hit, wind, back);
      b('shoulderL').rot = swing(a0, a1, hit, wind, back); // CÙNG dấu: cùng chọt tới
      b('elbowR').rot = 1.6 * (1 - S) + 0.05;
      b('elbowL').rot = 1.6 * (1 - S) + 0.05;
      torso.rot = -0.3 * W + 0.4 * S;
      head.rot = 0.12 * W - 0.22 * S;
      b('hipR').rot = -0.5 * S;
      b('hipL').rot = 0.55 * S;
      b('kneeR').rot = 0.3 + 0.6 * S;
      b('kneeL').rot = 0.3 + 0.4 * S;
      b('footR').rot = -0.2 * S;
      root.x = -5 * W + 13 * S; // phóng người tới rất xa
      root.y = 3 * W - 3 * S - 1.5 * settle;
      root.sx = 1 - 0.05 * W + 0.07 * S;
      root.sy = 1 + 0.05 * W - 0.05 * S;
      break;
    }

    // ───────────────────────── SONG QUYỀN (hai nắm đấm cùng phóng ra trước)
    case 'doubleFist': {
      const a0 = dir(BACK * 0.5, DOWN * 0.87); // thụt hai nắm về hông
      const a1 = dir(FWD * 0.98, UP * 0.2); // cùng phóng tới
      b('shoulderR').rot = swing(a0, a1, hit, wind, back);
      b('shoulderL').rot = swing(a0, a1, hit, wind, back); // CÙNG dấu
      b('elbowR').rot = 1.8 * (1 - S) + 0.05;
      b('elbowL').rot = 1.8 * (1 - S) + 0.05;
      torso.rot = -0.35 * W + 0.5 * S;
      head.rot = 0.14 * W - 0.24 * S;
      b('hipR').rot = -0.55 * S;
      b('hipL').rot = 0.6 * S;
      b('kneeR').rot = 0.25 + 0.7 * S;
      b('kneeL').rot = 0.25 + 0.7 * S;
      b('footR').rot = -0.25 * S;
      root.x = -5 * W + 14 * S;
      root.y = 4 * W - 3 * S - 1.2 * settle;
      root.sx = 1 - 0.06 * W + 0.08 * S;
      root.sy = 1 + 0.06 * W - 0.06 * S;
      break;
    }

    // ─────────────────────────────────────────────────────────── DỰ PHÒNG
    default: {
      const a0 = dir(BACK * 0.3, DOWN * 0.95);
      const a1 = dir(FWD, 0);
      b('shoulderR').rot = swing(a0, a1, hit, wind, back);
      b('shoulderL').rot = swing(a0, a1, hit, wind, back);
      b('elbowR').rot = 0.8 * (1 - S);
      b('elbowL').rot = 0.8 * (1 - S);
      torso.rot = 0.2 * S;
    }
  }
}
